import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Logo } from '@/components/brand/logo';
import { DemoChooser } from '@/components/demo-chooser';
import { LanguageSwitch } from '@/components/language-switch';
import { auth, isDemoPersona, type DemoPersona } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import { setOrgId } from '@/lib/org';

export const Route = createFileRoute('/demo')({
  validateSearch: (s: Record<string, unknown>): { as?: DemoPersona } =>
    isDemoPersona(s['as']) ? { as: s['as'] } : {},
  component: Demo,
});

// One sign-in per page load: React may run the effect twice, and a second redirect would replace
// the first one's stored state.
let started = false;

/**
 * Try the demo (document 06 W4, ADR-0020). With ?as=accountant or ?as=owner: straight to the
 * identity provider's demo sign-in for that persona. Without it: the choice of persona. Without
 * the demo sign-in this is the usual Log in.
 */
function Demo() {
  const navigate = useNavigate();
  const t = useT();
  const { as } = Route.useSearch();
  useEffect(() => {
    if (!auth.demo) {
      void navigate({ to: auth.enabled ? '/login' : '/dashboard' });
      return;
    }
    if (!as || started) return;
    started = true;
    // the demo opens on its own first client, not on one this browser had open before
    setOrgId(null);
    void auth.signInDemo(as).catch(() => {
      started = false;
      void navigate({ to: '/login' });
    });
  }, [as, navigate]);

  if (as || !auth.demo)
    return (
      <div className="grid min-h-screen place-items-center p-6">
        <div className="flex flex-col items-center gap-3">
          <Logo />
          <p className="text-sm text-muted-foreground">{t('publicPages.demo.opening')}</p>
        </div>
      </div>
    );
  return (
    <div className="grid min-h-screen place-items-center bg-muted/40 px-4 py-10">
      <div className="w-full max-w-2xl rounded-xl border bg-background p-6 shadow-sm sm:p-8">
        <div className="flex items-center justify-between gap-3">
          <Logo />
          <LanguageSwitch className="text-brand-ink" />
        </div>
        <h1 className="mt-6 text-2xl font-semibold text-brand-ink">
          {t('publicPages.demo.howExplore')}
        </h1>
        <p className="mt-1 mb-6 text-sm text-muted-foreground">{t('publicPages.demo.note')}</p>
        <DemoChooser />
      </div>
    </div>
  );
}
